import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "setup";
import { clearActiveSubmission } from "../../ActiveSubmissionSlice";
import { createSubmission, getActiveSubmission } from "../../../shared/submissionThunks";
import { clearUpload } from "../FileUpload/FileUploadSlice";
import { ColumnSource } from "../../sheetSources";

type ParsingState = {
  // Each worksheet's header columns and the sheet each came from; rebuilt on every pass through Parsing
  ParsedColumnSources: Record<string, ColumnSource[]> | null;
};

const initialState: ParsingState = {
  ParsedColumnSources: null,
};

export const parsingSlice = createSlice({
  name: "dataImportParsing",
  initialState,
  reducers: {
    setParsedColumnSources: (state, action: PayloadAction<Record<string, ColumnSource[]>>) => {
      state.ParsedColumnSources = action.payload;
    },
    // Mapping goes back through Parsing to read the headers again
    clearParsedColumnSources: (state) => {
      state.ParsedColumnSources = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Parsed columns describe the uploaded file(s): reset when the active submission
      // changes or the upload is discarded.
      .addCase(getActiveSubmission.pending, () => initialState)
      .addCase(createSubmission.fulfilled, () => initialState)
      .addCase(clearActiveSubmission, () => initialState)
      .addCase(clearUpload, () => initialState);
  },
});

export const { clearParsedColumnSources, setParsedColumnSources } = parsingSlice.actions;

export const selectParsedColumnSources = (state: RootState) => state.dataImportParsing.ParsedColumnSources;

// Each worksheet's column names, in file order
export const selectParsedColumns = createSelector([selectParsedColumnSources], (columnSources) =>
  columnSources
    ? Object.fromEntries(
        Object.entries(columnSources).map(([worksheet, sources]) => [
          worksheet,
          sources.map((source) => source.columnName),
        ])
      )
    : null
);

export default parsingSlice.reducer;
